import { BASE_ROLES_NAMES } from '../shared/constants.js';

const disallowGuest = session => !session.loggedIn || session.isPublicPortalGuest;

const importOnboardingView = session => {
  if (session.featureEnabled('skillOnboarding')) {
    return import('./main/views/onboarding/view-onboarding/view-onboarding.js');
  }
  if (session.hasFeature('readonly')) {
    return import('./shared/views/view-home/view-home.js');
  }
  return import('./shared/views/errors/view-not-found/view-not-found.js');
};

const importClientOnboardingView = session => {
  const tenant = session.tenant;
  const hasPermission = session.user.hasPermission(['settings:update']);
  const canAccessClientOnboarding = tenant?.canAccessPlgClientOnboarding?.() === true;

  if (hasPermission && canAccessClientOnboarding) {
    if (tenant?.isPreferredPlgTenant?.() === true) {
      return import('./main/views/client-onboarding/view-client-onboarding-simplified/view-client-onboarding-simplified.js');
    }
    return import('./main/views/client-onboarding/view-client-onboarding/view-client-onboarding.js');
  }
  return import('./shared/views/errors/view-not-found/view-not-found.js');
};

const routes = [
  {
    name: 'home',
    pattern: '',
    data: {
      importDynamicView: session => {
        return (session.featureEnabled('skillOnboarding')
          ? import('./main/views/profile/view-landing-page/view-landing-page.js')
          : import('./shared/views/view-home/view-home.js'));
      },
      getDynamicLayout: session => (session.tenant.hasTag('adminRoles') && !session.user.hasRole(BASE_ROLES_NAMES.USER) ? 'default' : 'full-width'),
      documentTitleLangKey: 'app-nova.documentTitle',
    },
  },
  {
    name: 'recommendations',
    pattern: 'recommendations',
    data: {
      importDynamicView: session => (session.featureEnabled('skillOnboarding')
        ? import('./main/views/profile/view-landing-page/view-landing-page.js')
        : import('./shared/views/errors/view-not-found/view-not-found.js')),
      layout: 'full-width',
      documentTitleLangKey: 'view-landing-page.documentTitle',
    },
  },
  {
    name: 'how-it-works',
    pattern: 'how-it-works',
    data: {
      importView: () => import('./shared/views/view-home/view-home.js'),
      layout: 'full-width',
      documentTitleLangKey: 'app-nova.documentTitle',
    },
  },
  {
    name: 'activities',
    pattern: 'activities',
    data: {
      importView: () => import('./main/views/activities/view-activities-dashboard/view-activities-dashboard.js'),
      documentTitleLangKey: 'view-activities.documentTitle',
    },
  },
  {
    name: 'activities-by-career',
    pattern: 'activities/skill-stream/career/:careerId',
    data: {
      importView: () => import('./main/views/activities/view-activity-category/view-activity-category.js'),
      documentTitleLangKey: 'view-activity-category.documentTitle.whileLoading',
    },
  },
  {
    name: 'activities-by-goals',
    pattern: 'activities/skill-stream/interested-goals/:goalId',
    data: {
      importView: () => import('./main/views/activities/view-activity-category/view-activity-category.js'),
      documentTitleLangKey: 'view-activity-category.documentTitle.whileLoading',
    },
  },
  {
    name: 'activities-by-skill-stream',
    pattern: 'activities/skill-stream/:category',
    data: {
      importView: () => import('./main/views/activities/view-activity-category/view-activity-category.js'),
      documentTitleLangKey: 'view-activity-category.documentTitle.whileLoading',
    },
  },
  {
    name: 'view-provider',
    pattern: 'providers/:providerId',
    data: {
      importView: () => import('./main/views/providers/view-provider/view-provider.js'),
      documentTitleLangKey: 'view-activity-category.documentTitle.whileLoading',
    },
  },
  {
    name: 'view-profile-root',
    pattern: 'profile',
    data: {
      layout: 'admin',
      importView: () => import('./main/views/profile/view-profile/view-profile.js'),
      documentTitleLangKey: 'view-profile.documentTitle',
    },
  },
  {
    name: 'view-profile',
    pattern: 'profile/:userId/:panel',
    data: {
      layout: 'admin',
      importView: () => import('./main/views/profile/view-profile/view-profile.js'),
      documentTitleLangKey: 'view-profile.documentTitle',
    },
  },
  {
    name: 'add-activity',
    pattern: 'activities/add',
    data: {
      importView: () => import('./admin/views/edit-activity/edit-activity.js'),
      documentTitleLangKey: 'edit-activity.documentTitle.addActivity',
    },
  },
  {
    name: 'import-activity',
    pattern: 'activities/import',
    data: {
      importView: () => import('./admin/views/activity-import/activity-import.js'),
      documentTitleLangKey: 'activity-import.documentTitle.stream',
    },
  },
  {
    name: 'edit-activity',
    pattern: 'activities/:id/edit',
    data: {
      importView: () => import('./admin/views/edit-activity/edit-activity.js'),
      documentTitleLangKey: 'edit-activity.documentTitle',
    },
  },
  {
    name: 'view-activity',
    pattern: 'activities/:id',
    data: {
      importView: () => import('./main/views/activities/view-activity/view-activity.js'),
      layout: 'full-width',
      documentTitleLangKey: 'view-activity.documentTitle.whileLoading',
    },
  },
  {
    name: 'magic-link',
    pattern: 'login/magic',
    data: {
      protected: () => false,
      importView: () => import('./shared/views/redirect-magic-link/redirect-magic-link.js'),
      layout: 'immersive',
      documentTitleLangKey: 'login.magiclink.documentTitle',
    },
  },
  {
    name: 'apply-activity',
    pattern: 'activities/:id/apply',
    data: {
      importView: () => import('./main/views/activities/apply-activity/apply-activity.js'),
      documentTitleLangKey: 'apply-activity.documentTitle',
    },
  },
  {
    name: 'review-requirements',
    pattern: 'activities/:original_id/:selected_id/apply/review-requirements',
    data: {
      importView: () => import('./main/views/applications/review-admission-requirements/review-admission-requirements.js'),
      documentTitleLangKey: 'admission-requirements.review.documentTitle',
    },
  },
  {
    name: 'review-application',
    pattern: 'activities/:original_id/:selected_id/apply/review',
    data: {
      importView: () => import('./main/views/applications/review-application/review-application.js'),
      documentTitleLangKey: 'review-application.documentTitle',
    },
  },
  {
    name: 'review-submit-own-request-application',
    pattern: 'activities/apply/review-submit-own-request',
    data: {
      importView: () => import('./main/views/applications/review-submit-own-request-application/review-submit-own-request-application.js'),
      documentTitleLangKey: 'review-application.documentTitle',
    },
  },
  {
    name: 'redirect-admin',
    pattern: 'admin',
    data: {
      type: 'redirect',
      url: '/admin/activities',
    },
  },
  {
    name: 'view-admin',
    pattern: 'admin/:panel',
    data: {
      importView: () => import('./admin/views/view-admin/view-admin.js'),
      layout: 'admin',
      documentTitleLangKey: 'view-admin.documentTitle',
    },
  },
  {
    name: 'add-tenant',
    pattern: 'tenants/add/:type',
    data: {
      importView: () => import('./admin/views/edit-tenant/edit-tenant.js'),
      documentTitleLangKey: 'edit-tenant.documentTitle.addTenant',
    },
  },
  {
    name: 'edit-tenant',
    pattern: 'tenants/:id/edit/:panel',
    data: {
      importView: () => import('./admin/views/edit-tenant/edit-tenant.js'),
      layout: 'admin',
      documentTitleLangKey: 'edit-tenant.documentTitle',
    },
  },
  {
    name: 'edit-tenant-redirect',
    pattern: 'tenants/:id/edit',
    data: {
      type: 'redirect',
      url: '/tenants/:id/edit/general',
    },
  },
  {
    name: 'employee-search',
    pattern: 'employee-search',
    data: {
      importView: () => import('./main/views/general/employee-search/employee-search.js'),
      documentTitleLangKey: 'employee-search.documentTitle',
    },
  },
  {
    name: 'analytics',
    pattern: 'analytics',
    data: {
      importView: () => import('./shared/views/report/view-report-dashboard/view-report-dashboard.js'),
      documentTitleLangKey: 'view-report-dashboard.documentTitle',
    },
  },
  {
    name: 'redirect-settings',
    pattern: 'settings',
    data: {
      type: 'redirect',
      url: '/settings/user-management',
    },
  },
  {
    name: 'settings',
    pattern: 'settings/:panel',
    data: {
      importView: () => import('./settings/views/view-settings/view-settings.js'),
      layout: 'admin',
      documentTitleLangKey: 'view-settings.documentTitle',
    },
  },
  {
    name: 'settings',
    pattern: 'settings/:panel/:subview',
    data: {
      importView: () => import('./settings/views/view-settings/view-settings.js'),
      layout: 'admin',
      documentTitleLangKey: 'view-settings.documentTitle',
    },
  },
  {
    name: 'view-applications',
    pattern: 'requests',
    data: {
      protected: disallowGuest,
      importView: () => import('./main/views/applications/view-applications/view-applications.js'),
      documentTitleLangKey: 'view-applications.documentTitle',
    },
  },
  {
    name: 'submit-own-request',
    pattern: 'requests/submit-own-request',
    data: {
      protected: disallowGuest,
      importView: () => import('./main/views/applications/submit-own-request/submit-own-request.js'),
      documentTitleLangKey: 'submit-own-request.documentTitle',
    },
  },
  {
    name: 'review-application',
    pattern: 'requests/submit-own-request/review',
    data: {
      protected: disallowGuest,
      importView: () => import('./main/views/applications/review-application/review-application.js'),
      documentTitleLangKey: 'review-application.documentTitle',
    },
  },
  {
    name: 'view-application',
    pattern: 'requests/:uuid',
    data: {
      protected: disallowGuest,
      importView: () => import('./main/views/applications/view-application/view-application.js'),
      documentTitleLangKey: 'view-application.documentTitle',
    },
  },
  {
    name: 'view-application-account-connection',
    pattern: 'requests/:uuid/account-connection',
    data: {
      protected: disallowGuest,
      importView: () => import('./main/views/applications/view-account-connection/view-account-connection.js'),
      documentTitleLangKey: 'view-application.documentTitle',
    },
  },
  {
    name: 'contact-support',
    pattern: 'contact-support',
    data: {
      importView: () => import('./main/views/general/contact-support/contact-support.js'),
      documentTitleLangKey: 'contact-support.documentTitle',
    },
  },
  {
    name: 'login',
    pattern: 'login',
    data: {
      protected: () => false,
      importView: () => import(/* webpackPreload: true */ './shared/views/view-login/view-login.js'),
      layout: 'immersive',
      documentTitleLangKey: 'view-login.documentTitle',
    },
  },
  {
    name: 'login-details',
    pattern: 'login-details',
    data: {
      importView: () => import('./shared/components/login/login-collect-details/login-collect-details.js'),
      layout: 'immersive',
      documentTitleLangKey: 'view-login-details.documentTitle',
    },
  },
  { // This is a legacy view that I would like to replace with the above view at some point soon
    name: 'get-user-details',
    pattern: 'login/get-user-details',
    data: {
      importView: () => import('./shared/components/general/get-user-details/get-user-details.js'),
      layout: 'immersive',
      documentTitleLangKey: 'view-login-details.documentTitle',
    },
  },
  {
    name: 'not-found',
    pattern: '*',
    data: {
      protected: () => false,
      importView: () => import('./shared/views/errors/view-not-found/view-not-found.js'),
      documentTitleLangKey: 'view-not-found.documentTitle',
    },
  },
  {
    name: 'error',
    pattern: 'error',
    data: {
      protected: () => false,
      importView: () => import(/* webpackPreload: true */ './shared/views/errors/view-error-loading/view-error-loading.js'),
      layout: 'immersive',
      documentTitleLangKey: 'view-error-loading.documentTitle',
    },
  },
  {
    name: 'maintenance',
    pattern: 'maintenance',
    data: {
      protected: () => false,
      importView: () => import('./shared/views/errors/view-maintenance-mode/view-maintenance-mode.js'),
      layout: 'immersive',
      documentTitleLangKey: 'view-error-loading.documentTitle',
    },
  },
  {
    name: 'view-application-confirmation',
    pattern: 'requests/:uuid/confirmation',
    data: {
      importView: () => import('./main/views/applications/view-application-confirmation/view-application-confirmation.js'),
      documentTitleLangKey: 'view-application.documentTitle',
    },
  },
  {
    name: 'thankyou-signup',
    pattern: 'thankyou-signup',
    data: {
      layout: 'full-width',
      importView: () => import('./shared/components/login/thankyou-signup/thankyou-signup.js'),
      documentTitleLangKey: 'thankyou-page.documentTitle',
    },
  },
  {
    name: 'auth-error',
    pattern: 'auth-error',
    data: {
      protected: () => false,
      layout: 'full-width',
      importView: () => import('./shared/components/login/auth-error/auth-error.js'),
      documentTitleLangKey: '"view-error-loading.documentTitle"',
    },
  },
  {
    name: 'onboarding',
    pattern: 'onboarding',
    data: {
      importDynamicView: importOnboardingView,
      documentTitleLangKey: 'view-onboarding.documentTitle',
    },
  },
  {
    name: 'client-onboarding',
    pattern: 'client-onboarding',
    data: {
      importDynamicView: importClientOnboardingView,
      documentTitleLangKey: 'view-client-onboarding.documentTitle',
      getDynamicLayout: session => (session.tenant.hasTag('plgTenant') ? 'immersive' : 'full-width'),
    },
  },
  {
    name: 'error-not-allowed',
    pattern: '403',
    data: {
      importView: () => import('./shared/views/errors/view-not-allowed/view-not-allowed.js'),
      documentTitleLangKey: 'view-error-loading.documentTitle',
    },
  },
];

export default routes.map(route => {
  // Set all routed to protected unless explicitly set to false
  if (!route.data.protected) {
    route.data.protected = session => !session.loggedIn;
  }
  return route;
});
